import { useFieldArray, useForm } from 'react-hook-form';
import { SHARE_REFERRAL } from '../queries/referral';
import { useMutation } from '@apollo/client';
import { useState } from 'react';

export const EMAIL_FIELDS_NAME = 'emails';
export const MAX_EMAILS_ALLOWED = 5;

export const useShareReferralForm = () => {
  const [submitState, setSubmitState] = useState<'success' | 'error' | null>(
    null,
  );

  const [shareReferral] = useMutation(SHARE_REFERRAL);

  const {
    register,
    handleSubmit,
    formState: { dirtyFields, isValid, isSubmitting, errors },
    control,
    reset,
    watch,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      emails: [{ email: '' }],
    },
  });

  const handleReferralSubmit = handleSubmit(async ({ emails }) => {
    await shareReferral({
      variables: {
        friendEmails: emails.map(({ email }) => email),
      },
    })
      .then(() => {
        setSubmitState('success');
      })
      .catch(() => {
        setSubmitState('error');
      });
  });

  const resetSubmitState = () => {
    reset();
    setSubmitState(null);
  };

  const {
    fields: emailFields,
    append,
    remove,
  } = useFieldArray({ name: EMAIL_FIELDS_NAME, control });

  const isMaxEmailsAllowedReached = emailFields.length === MAX_EMAILS_ALLOWED;

  const addEmailField = () => {
    if (!isMaxEmailsAllowedReached) {
      append({ email: '' });
    }
  };

  const removeEmailField = (index: number) => {
    if (emailFields.length !== 1) {
      remove(index);
    }
  };

  const getFieldErrorMessage = (index: number): string | undefined => {
    return (errors.emails?.[index]?.email?.message ?? undefined) as
      | string
      | undefined;
  };

  return {
    isSubmitting,
    submitState,
    isDirty: dirtyFields,
    isValid,
    isMaxEmailsAllowedReached,
    emailFields,
    register,
    handleSubmit: handleReferralSubmit,
    resetSubmitState,
    addEmailField,
    removeEmailField,
    getFieldErrorMessage,
    control,
    watch,
  };
};
